import { marked } from "marked";
import DOMPurify from "dompurify";

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Escapes for use inside a double-quoted HTML attribute and blocks
// script-executing URL schemes (marked's own href validation doesn't
// cover this custom renderer).
function safeHref(href) {
  const trimmed = String(href ?? "").trim();
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return "#";
  return escapeHtml(trimmed);
}

marked.use({
  gfm: true,
  breaks: true,
  renderer: {
    heading({ tokens, depth }) {
      if (depth > 3) return false;
      const text = this.parser.parseInline(tokens);
      const cls =
        depth === 1
          ? "font-bold text-base mt-2"
          : depth === 2
            ? "font-semibold mt-1.5"
            : "font-medium text-base-content/70 mt-1";
      return `<p class="${cls}">${text}</p>\n`;
    },

    paragraph({ tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<p class="leading-relaxed">${text}</p>\n`;
    },

    link({ href, tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<a href="${safeHref(href)}" target="_blank" rel="noopener noreferrer" class="link link-primary">${text}</a>`;
    },

    codespan({ text }) {
      return `<code class="font-mono text-xs bg-base-300 px-0.5 rounded">${escapeHtml(text)}</code>`;
    },

    list(token) {
      const tag = token.ordered ? "ol" : "ul";
      const cls = token.ordered
        ? "list-decimal list-inside my-1 space-y-0.5"
        : "list-disc list-inside my-1 space-y-0.5";
      let body = "";
      for (const item of token.items) {
        body += this.listitem(item);
      }
      return `<${tag} class="${cls}">\n${body}</${tag}>\n`;
    },
  },
});

export function renderMarkdown(raw) {
  if (!raw) return "";
  return DOMPurify.sanitize(marked.parse(raw));
}
